<template>
  <div class="dashboard">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>
    
    <!-- 数据统计区域 -->
    <div v-else class="data-section">
      <!-- 第一层：今日待验证 -->
      <div class="top-row">
        <div class="top-card unchecked-card">
          <div class="card-head">
            <h4>今日待验证</h4>
            <el-button size="mini" type="text" icon="el-icon-refresh" @click="fetchUnchecked" class="refresh-btn"></el-button>
          </div>
          <div class="card-body">
            <div v-if="uncheckedLoading" class="mini-loading"><i class="el-icon-loading"></i></div>
            <div v-else class="names-grid">
              <div
                v-for="item in uncheckedNames"
                :key="item.id"
                class="name-card"
                @click="editName(item)">
                <div class="name-uyghur">{{ item.name_uyghur }}</div>
                <i class="status-icon" :class="item.has_checked ? 'el-icon-check' : 'el-icon-warning'"></i>
              </div>
              <div v-if="!uncheckedNames || uncheckedNames.length===0" class="empty">暂无数据</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 第二层：最近查看 + 最多查看 -->
      <div class="bottom-row">
        <div class="views-grid">
          <!-- 最近查看 -->
          <div class="top-card">
            <div class="card-head">
              <h4>最近查看</h4>
              <el-button size="mini" type="text" icon="el-icon-refresh" @click="fetchViews" class="refresh-btn"></el-button>
            </div>
            <div class="card-body">
              <div v-if="recentLoading" class="mini-loading"><i class="el-icon-loading"></i></div>
              <div v-else class="views-list">
                <div
                  v-for="item in recentList"
                  :key="item.name_id"
                  class="views-item"
                  @click="editName(item)">
                  <div class="name-uyghur">{{ item.name_uyghur || item.name }}</div>
                  <div class="item-meta">{{ formatDate(item.last_viewed) }}</div>
                </div>
                <div v-if="!recentList || recentList.length===0" class="empty">暂无数据</div>
              </div>
            </div>
          </div>

          <!-- 最多查看 -->
          <div class="top-card top-view-card">
            <div class="card-head">
              <h4>最多查看</h4>
              <el-button size="mini" type="text" icon="el-icon-refresh" @click="fetchViews" class="refresh-btn"></el-button>
            </div>
            <div class="card-body">
              <div v-if="topLoading" class="mini-loading"><i class="el-icon-loading"></i></div>
              <div v-else class="views-list">
                <div
                  v-for="item in topList"
                  :key="item.name_id"
                  class="views-item"
                  @click="editName(item)">
                  <div class="name-uyghur">{{ item.name_uyghur || item.name }}</div>
                  <div class="item-meta">{{ item.views_in_window || item.views || item.view_count || 0 }} 次</div>
                </div>
                <div v-if="!topList || topList.length===0" class="empty">暂无数据</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Names统计 -->
      <div class="stat-group">
        <div class="stat-group-header">
          <div class="stat-total">
            <div class="total-label">TOTAL NAMES</div>
            <div class="total-value">{{ stats.totalNames }}</div>
          </div>
          <div class="gender-stats">
            <div 
              class="gender-item" 
              v-for="item in genderNames" 
              :key="item.name"
            >
              <span class="gender-name">{{ item.name }}</span>
              <span class="gender-value">{{ item.value }}</span>
              <span class="gender-percentage">{{ getPercentage(item.value, genderNames) }}%</span>
            </div>
          </div>
        </div>
        <div class="stat-group-content column-2">
          <div class="stat-card chart-card">
            <div class="card-header">
              <h3 class="card-title">Origins:</h3>
            </div>
            <div class="chart-wrapper">
              <canvas ref="originChart"></canvas>
            </div>
            <div class="chart-legend" :class="getLegendClass(originNames.length)">
              <div 
                class="legend-item" 
                v-for="(item, index) in originNames" 
                :key="item.name"
                @mouseenter="handleLegendHover('originChart', index)"
                @mouseleave="handleLegendLeave('originChart')"
              >
                <span class="legend-color" :style="{ background: getColor('origin', index) }"></span>
                <span class="legend-name">{{ item.name }}</span>
                <span class="legend-value">{{ item.value }}</span>
                <span class="legend-percentage">{{ getPercentage(item.value, originNames) }}%</span>
              </div>
            </div>
          </div>

          <div class="stat-card chart-card">
            <div class="card-header">
              <h3 class="card-title">Topics 分布</h3>
            </div>
            <div class="chart-wrapper">
              <canvas ref="tagChart"></canvas>
            </div>
            <div class="chart-legend" :class="getLegendClass(tagNames.length)">
              <div 
                class="legend-item" 
                v-for="(item, index) in tagNames" 
                :key="item.name"
                @mouseenter="handleLegendHover('tagChart', index)"
                @mouseleave="handleLegendLeave('tagChart')"
              >
                <span class="legend-color" :style="{ background: getColor('tag', index) }"></span>
                <span class="legend-name">{{ item.name }}</span>
                <span class="legend-value">{{ item.value }}</span>
                <span class="legend-percentage">{{ getPercentage(item.value, tagNames) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quotes统计 -->
      <div class="stat-group">
        <div class="stat-group-header">
          <div class="stat-total">
            <div class="total-label">TOTAL QUOTES</div>
            <div class="total-value">{{ stats.totalQuotes }}</div>
          </div>
        </div>
        <div class="stat-group-content column-2">
          <div class="stat-card chart-card">
            <div class="card-header">
              <h3 class="card-title">语言分布</h3>
            </div>
            <div class="chart-wrapper">
              <canvas ref="languageChart"></canvas>
            </div>
            <div class="chart-legend" :class="getLegendClass(languageQuotes.length)">
              <div 
                class="legend-item" 
                v-for="(item, index) in languageQuotes" 
                :key="item.name"
                @mouseenter="handleLegendHover('languageChart', index)"
                @mouseleave="handleLegendLeave('languageChart')"
              >
                <span class="legend-color" :style="{ background: getColor('language', index) }"></span>
                <span class="legend-name">{{ item.name }}</span>
                <span class="legend-value">{{ item.value }}</span>
                <span class="legend-percentage">{{ getPercentage(item.value, languageQuotes) }}%</span>
              </div>
            </div>
          </div>

          <div class="stat-card chart-card">
            <div class="card-header">
              <h3 class="card-title">Topics 分布</h3>
            </div>
            <div class="chart-wrapper">
              <canvas ref="topicsChart"></canvas>
            </div>
            <div class="chart-legend" :class="getLegendClass(topicsQuotes.length)">
              <div 
                class="legend-item" 
                v-for="(item, index) in topicsQuotes" 
                :key="item.name"
                @mouseenter="handleLegendHover('topicsChart', index)"
                @mouseleave="handleLegendLeave('topicsChart')"
              >
                <span class="legend-color" :style="{ background: getColor('topics', index) }"></span>
                <span class="legend-name">{{ item.name }}</span>
                <span class="legend-value">{{ item.value }}</span>
                <span class="legend-percentage">{{ getPercentage(item.value, topicsQuotes) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 快捷入口区域 -->
    <div v-if="!loading" class="quick-access-section">
      <div 
        class="category-card" 
        v-for="category in quickAccessCategories" 
        :key="category.title"
      >
        <div class="category-header">
          <i :class="category.icon"></i>
          <span class="category-title">{{ category.title }}</span>
        </div>
        <div class="category-items">
          <div 
            class="access-item" 
            v-for="item in category.items" 
            :key="item.path"
            @click="navigateTo(item.path)"
          >
            <i :class="item.icon"></i>
            <span class="access-title">{{ item.title }}</span>
          </div>
        </div>
      </div>
    </div>

     <!-- 编辑弹窗（同 Names.vue 格式） -->
    <el-dialog 
      title="名字管理" 
      :visible.sync="dialogFormVisible" 
      :width="'600px'"
      :fullscreen="false"
      :close-on-click-modal="false">
      <el-form label-width="90px" size="small" class="edit-form">
        <el-row :gutter="15">
          <el-col :span="12" :xs="24">
            <el-form-item label="Uyghur">
              <el-input v-model="form.name_uyghur" placeholder="e.g. ئىسىم" autocomplete="off"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Chinese">
              <el-input v-model="form.name_chinese" placeholder="例如：名字" autocomplete="off"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Latin">
              <el-input v-model="form.name_latin" placeholder="e.g. Name" autocomplete="off"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Gender">
              <el-select v-model="form.gender" placeholder="Select" clearable>
                <el-option label="Male" value="male"></el-option>
                <el-option label="Female" value="female"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="Meaning">
              <el-input v-model="form.name_meaning" type="textarea" :rows="3" placeholder="Name meaning" class="rtl-textarea"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="Description">
              <el-input v-model="form.name_desc" type="textarea" :rows="3" placeholder="Brief description" class="rtl-textarea"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Origin">
              <el-select v-model="form.origin" placeholder="请选择Origin" clearable filterable>
                <el-option 
                  v-for="item in originList" 
                  :key="item.id" 
                  :label="item.name" 
                  :value="item.name">
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Tag">
              <el-select v-model="form.tag" placeholder="请选择Tag" clearable filterable>
                <el-option 
                  v-for="item in tagList" 
                  :key="item.id" 
                  :label="item.name" 
                  :value="item.name">
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Visible">
              <el-switch
                v-model="form.is_member_only"
                active-text="会员"
                inactive-text="普通"
                :active-value="true"
                :inactive-value="false">
              </el-switch>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Hidden">
              <el-switch
                v-model="form.is_hidden"
                active-text="隐藏"
                inactive-text="显示"
                :active-value="true"
                :inactive-value="false">
              </el-switch>
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24">
            <el-form-item label="Checked">
              <el-switch
                v-model="form.has_checked"
                active-text="已核实"
                inactive-text="未核实"
                :active-value="true"
                :inactive-value="false">
              </el-switch>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editCancel">取 消</el-button>
        <el-button type="primary" @click="editDone">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Chart from 'chart.js'

export default {
  name: 'Bashbet',
  data() {
    return {
      loading: false,
      stats: {
        totalQuotes: 0,
        totalNames: 0
      },
      // controls for recent/top views
      recentList: [],
      topList: [],
      recentLoading: false,
      topLoading: false,
      viewsLoading: false,
      charts: {},
      quickAccessCategories: [
        {
          title: '内容管理',
          icon: 'el-icon-document',
          items: [
            {
              title: 'Content',
              path: '/admin/quotes/zh',
              icon: 'el-icon-edit-outline'
            },
            {
              title: 'Topics',
              path: '/admin/topics',
              icon: 'el-icon-folder-opened'
            },
            {
              title: 'Themes',
              path: '/admin/themes',
              icon: 'el-icon-brush'
            }
          ]
        },
        {
          title: '用户管理',
          icon: 'el-icon-user',
          items: [
            {
              title: 'Mini Users',
              path: '/admin/mini-users',
              icon: 'el-icon-user-solid'
            },
            {
              title: 'VIP Types',
              path: '/admin/vip-types',
              icon: 'el-icon-star-on'
            },
            {
              title: 'Memberships',
              path: '/admin/memberships',
              icon: 'el-icon-tickets'
            }
          ]
        },
        {
          title: '数据管理',
          icon: 'el-icon-database',
          items: [
            {
              title: 'Namzar',
              path: '/admin/names',
              icon: 'el-icon-collection-tag'
            },
            {
              title: 'Origins',
              path: '/admin/origins',
              icon: 'el-icon-location-outline'
            },
            {
              title: 'Tags',
              path: '/admin/tags',
              icon: 'el-icon-price-tag'
            }
          ]
        },
        {
          title: '系统设置',
          icon: 'el-icon-setting',
          items: [
            {
              title: 'Admin Users',
              path: '/admin/admin-users',
              icon: 'el-icon-user'
            }
          ]
        }
      ],
      languageQuotes: [],
      languageColors: [
        'rgba(100, 181, 246, 0.85)',
        'rgba(129, 199, 132, 0.85)',
        'rgba(255, 183, 77, 0.85)'
      ],
      topicsQuotes: [],
      topicsColors: [
        'rgba(144, 202, 249, 0.85)',
        'rgba(129, 199, 132, 0.85)',
        'rgba(255, 183, 77, 0.85)',
        'rgba(186, 104, 200, 0.85)',
        'rgba(79, 195, 247, 0.85)'
      ],
      originNames: [],
      originColors: [
        'rgba(100, 181, 246, 0.85)',
        'rgba(129, 199, 132, 0.85)',
        'rgba(255, 183, 77, 0.85)',
        'rgba(186, 104, 200, 0.85)',
        'rgba(79, 195, 247, 0.85)'
      ],
      tagNames: [],
      tagColors: [
        'rgba(144, 202, 249, 0.85)',
        'rgba(129, 199, 132, 0.85)',
        'rgba(255, 183, 77, 0.85)',
        'rgba(186, 104, 200, 0.85)',
        'rgba(79, 195, 247, 0.85)'
      ],
      genderNames: [],
      genderColors: [
        'rgba(100, 181, 246, 0.85)',
        'rgba(255, 128, 171, 0.85)'
      ],
      uncheckedNames: [],
      uncheckedLoading: false,
      dialogFormVisible: false,
      form: {},
      originList: [],
      tagList: []
    }
  },
  created() {
    this.fetchStatistics()
    this.fetchViews()
    this.fetchUnchecked()
    this.getOrigins()
    this.getTags()
  },
  mounted() {
    // 图表初始化将在数据加载后执行
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(chart => {
      if (chart) {
        chart.destroy()
      }
    })
  },
  methods: {
    // 获取最近/最多查看列表
    fetchViews() {
      // 防止重复请求
      if (this.viewsLoading) {
        return
      }
      
      this.viewsLoading = true
      this.recentLoading = true
      this.topLoading = true

      const params = {}
      // 允许 days 为 0 表示不传 window（按总计排序），否则传 days
      if (this.viewsDays !== null && this.viewsDays !== undefined && this.viewsDays !== 0) {
        params.days = this.viewsDays
      }

      Promise.all([
        this.request.get('/admin/dashboard/names/recent', { params }),
        this.request.get('/admin/dashboard/names/top', { params })
      ])
        .then(([recentRes, topRes]) => {
          const recentData = recentRes?.data || recentRes || []
          const topData = topRes?.data || topRes || []
          this.recentList = Array.isArray(recentData) ? recentData : recentData.items || []
          this.topList = Array.isArray(topData) ? topData : topData.items || []
        })
        .catch(err => {
          console.error('获取查看列表失败:', err)
          this.$message.error(err?.message || '获取查看列表失败')
        })
        .finally(() => {
          this.viewsLoading = false
          this.recentLoading = false
          this.topLoading = false
        })
    },

    formatDate(ts) {
      if (!ts) return ''
      try {
        const d = new Date(ts)
        if (isNaN(d.getTime())) return ts
        // 简化为 MM/dd HH:mm 格式
        const month = (d.getMonth() + 1).toString().padStart(2, '0')
        const day = d.getDate().toString().padStart(2, '0')
        const hours = d.getHours().toString().padStart(2, '0')
        const minutes = d.getMinutes().toString().padStart(2, '0')
        return `${month}/${day} ${hours}:${minutes}`
      } catch (e) {
        return ts
      }
    },
    fetchUnchecked() {
      // 防止重复请求
      if (this.uncheckedLoading) {
        return
      }
      
      this.uncheckedLoading = true
      this.request
        .get('/admin/dashboard/names/unchecked')
        .then(res => {
          const list = res?.data || res || []
          this.uncheckedNames = Array.isArray(list) ? list : []
        })
        .catch(err => {
          console.error('获取未核实名字失败:', err)
          this.$message.error(err?.message || '获取未核实名字失败')
        })
        .finally(() => {
          this.uncheckedLoading = false
        })
    },
    editName(item) {
      // 处理不同数据源的字段差异
      this.form = {
        ...item,
        // 统一ID字段
        id: item.id || item.name_id,
        // 统一字段处理
        is_member_only: item.is_member_only === 1 || item.is_member_only === true || false,
        is_hidden: item.is_hidden === 1 || item.is_hidden === true || false,
        has_checked: item.has_checked === 1 || item.has_checked === true || false,
        // 统一名称字段
        name_uyghur: item.name_uyghur || item.name,
        name_chinese: item.name_chinese || '',
        name_latin: item.name_latin || '',
        name_meaning: item.name_meaning || '',
        name_desc: item.name_desc || '',
        gender: item.gender || '',
        origin: item.origin || '',
        tag: item.tag || ''
      }
      this.dialogFormVisible = true
    },

    editDone() {
      const submitData = {
        ...this.form,
        is_member_only: this.form.is_member_only === true || this.form.is_member_only === 1,
        is_hidden: this.form.is_hidden === true || this.form.is_hidden === 1,
        has_checked: this.form.has_checked === true || this.form.has_checked === 1
      }
      if (this.form.id) submitData.id = this.form.id

      this.request.post('/namzar/admin/names/edit', submitData)
        .then(res => {
          // 更宽松的成功判断
          const isSuccess = res && (
            res.code === 200 ||
            res.success === true ||
            res.data !== undefined ||
            (typeof res === 'object' && !res.error)
          )

          if (isSuccess) {
            this.$message.success('保存成功')
            this.dialogFormVisible = false
            // 刷新所有相关数据
            Promise.all([
              this.fetchUnchecked(),
              this.fetchViews(),
              this.fetchStatistics()
            ]).then(() => {
              console.log('所有数据刷新完成')
            }).catch(err => {
              console.error('数据刷新失败:', err)
              this.$message.error('数据刷新失败，请手动刷新页面')
            })
          } else {
            console.error('保存失败，响应:', res)
            this.$message.error(res?.message || '保存失败，请检查数据')
          }
        })
        .catch(err => {
          console.error('编辑请求失败:', err)
          const errorMsg = err?.response?.data?.message ||
                          err?.response?.data?.error ||
                          err?.message ||
                          '保存失败，请重试'
          this.$message.error(errorMsg)
        })
    },
    editCancel() {
      this.dialogFormVisible = false
    },
    getOrigins(){
      this.request.get('/namzar/admin/origins').then(res => {
        if (res.code === 200) {
          this.originList = Array.isArray(res.data) ? res.data : []
        }
      }).catch(res => {
        console.error('获取Origin列表失败:', res?.message)
      })
    },
    getTags(){
      this.request.get('/namzar/admin/tags').then(res => {
        if (res.code === 200) {
          this.tagList = Array.isArray(res.data) ? res.data : []
        }
      }).catch(res => {
        console.error('获取Tag列表失败:', res?.message)
      })
    },
    fetchStatistics() {
      this.loading = true
      this.request
        .get('/admin/dashboard/statistics')
        .then(res => {
          const data = res?.data || {}
          
          // 更新总统计数据
          if (data.summary) {
            this.stats.totalNames = data.summary.totalNames || 0
            this.stats.totalQuotes = data.summary.totalQuotes || 0
          }
          
          // 更新 Names 统计
          if (data.namesStatistics) {
            this.originNames = Array.isArray(data.namesStatistics.origins) 
              ? data.namesStatistics.origins 
              : []
            this.tagNames = Array.isArray(data.namesStatistics.tags) 
              ? data.namesStatistics.tags 
              : []
            this.genderNames = Array.isArray(data.namesStatistics.genders) 
              ? data.namesStatistics.genders 
              : []
          }
          
          // 更新 Quotes 统计
          if (data.quotesStatistics) {
            this.languageQuotes = Array.isArray(data.quotesStatistics.languages) 
              ? data.quotesStatistics.languages 
              : []
            this.topicsQuotes = Array.isArray(data.quotesStatistics.topics) 
              ? data.quotesStatistics.topics 
              : []
          }
          
          // 数据加载完成后初始化图表 - 使用双重 nextTick 确保 DOM 完全渲染
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.initCharts()
            })
          })
        })
        .catch(err => {
          console.error('获取统计数据失败:', err)
          this.$message.error(err?.message || '获取统计数据失败，请稍后重试')
          // 即使失败也尝试初始化图表（使用空数据）
          this.$nextTick(() => {
            this.initCharts()
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    navigateTo(path) {
      this.$router.push(path)
    },
    getPercentage(value, data) {
      if (!data || data.length === 0) return '0.0'
      const total = data.reduce((sum, item) => sum + (item.value || 0), 0)
      if (total === 0) return '0.0'
      return ((value / total) * 100).toFixed(1)
    },
    getLegendClass(count) {
      if (count <= 3) {
        return 'legend-single'
      } else if (count <= 6) {
        return 'legend-double'
      } else if (count <= 12) {
        return 'legend-triple'
      } else {
        return 'legend-quad'
      }
    },
    // 图例hover高亮对应图表元素
    handleLegendHover(chartName, index) {
      const chart = this.charts[chartName]
      if (!chart || !chart.getDatasetMeta(0)) return

      const chartType = chart.config.type
      const meta = chart.getDatasetMeta(0)
      const dataset = chart.config.data.datasets[0]
      
      if (chartType === 'bar') {
        // 柱状图的 hover 处理 - 高亮对应的柱子
        meta.data.forEach((el, i) => {
          if (i === index) {
            el._model.backgroundColor = dataset.hoverBackgroundColor ? dataset.hoverBackgroundColor[i] : dataset.backgroundColor[i].replace('0.85', '1')
            el._model.borderColor = 'rgba(255, 255, 255, 0.8)'
            el._model.borderWidth = 2
          } else {
            el._model.backgroundColor = dataset.backgroundColor[i]
            el._model.borderColor = dataset.borderColor ? dataset.borderColor[i] : 'rgba(255, 255, 255, 0.15)'
            el._model.borderWidth = 1
          }
        })
        const activeEl = meta.data[index]
        if (activeEl) {
          chart.tooltip._active = [activeEl]
          chart.tooltip.update(true)
          chart.draw()
        }
      } else {
        // 饼图/环形图的 hover 处理
        meta.data.forEach((el, i) => {
          el._model.hoverOffset = 8
          el._model.borderWidth = 3
          el._model.borderColor = 'rgba(255, 255, 255, 0.15)'
        })

        const activeEl = meta.data[index]
        if (activeEl) {
          activeEl._model.hoverOffset = 15
          activeEl._model.borderWidth = 5
          activeEl._model.borderColor = 'rgba(255, 255, 255, 0.6)'
          
          chart.tooltip._active = [activeEl]
          chart.tooltip.update(true)
          chart.draw()
        }
      }
    },
    // 离开图例恢复样式
    handleLegendLeave(chartName) {
      const chart = this.charts[chartName]
      if (!chart || !chart.getDatasetMeta(0)) return

      const chartType = chart.config.type
      const meta = chart.getDatasetMeta(0)
      const dataset = chart.config.data.datasets[0]
      
      if (chartType === 'bar') {
        // 柱状图的恢复
        meta.data.forEach((el, i) => {
          el._model.backgroundColor = dataset.backgroundColor[i]
          el._model.borderColor = dataset.borderColor ? dataset.borderColor[i] : 'rgba(255, 255, 255, 0.15)'
          el._model.borderWidth = 1
        })
        chart.tooltip._active = []
        chart.tooltip.update(false)
        chart.draw()
      } else {
        // 饼图/环形图的恢复
        meta.data.forEach(el => {
          el._model.hoverOffset = 8
          el._model.borderWidth = 3
          el._model.borderColor = 'rgba(255, 255, 255, 0.15)'
        })
        
        chart.tooltip._active = []
        chart.tooltip.update(false)
        chart.draw()
      }
    },
    initCharts() {
      // 使用 setTimeout 确保 DOM 完全渲染
      setTimeout(() => {
        this.createChart('originChart', this.originNames, this.originColors, 'pie')  // doughnut
        this.createChart('tagChart', this.tagNames, this.tagColors, 'bar')
        this.createChart('languageChart', this.languageQuotes, this.languageColors, 'pie')
        this.createChart('topicsChart', this.topicsQuotes, this.topicsColors, 'bar')
      }, 100)
    },
    getColor(type, index) {
      // 根据类型获取对应的颜色数组
      let colorArray = []
      switch(type) {
        case 'origin':
          colorArray = this.originColors
          break
        case 'tag':
          colorArray = this.tagColors
          break
        case 'gender':
          colorArray = this.genderColors
          break
        case 'language':
          colorArray = this.languageColors
          break
        case 'topics':
          colorArray = this.topicsColors
          break
        default:
          colorArray = this.originColors
      }
      
      // 如果索引超出范围，使用扩展颜色
      if (index < colorArray.length) {
        return colorArray[index]
      } else {
        return this.getExtendedColor(index - colorArray.length)
      }
    },
    getExtendedColor(index) {
      // 扩展颜色方案 - 支持更多颜色
      const extendedColors = [
        'rgba(100, 181, 246, 0.85)',   // 蓝色
        'rgba(129, 199, 132, 0.85)',   // 绿色
        'rgba(255, 183, 77, 0.85)',    // 橙色
        'rgba(186, 104, 200, 0.85)',   // 紫色
        'rgba(79, 195, 247, 0.85)',    // 浅蓝
        'rgba(255, 112, 67, 0.85)',    // 深橙
        'rgba(77, 182, 172, 0.85)',    // 青绿
        'rgba(156, 39, 176, 0.85)',    // 深紫
        'rgba(255, 152, 0, 0.85)',     // 金黄
        'rgba(244, 67, 54, 0.85)',     // 红色
        'rgba(63, 81, 181, 0.85)',     // 靛蓝
        'rgba(0, 150, 136, 0.85)',     // 青色
        'rgba(121, 85, 72, 0.85)',     // 棕色
        'rgba(233, 30, 99, 0.85)',     // 粉红
        'rgba(3, 169, 244, 0.85)',     // 天蓝
        'rgba(205, 220, 57, 0.85)',    // 黄绿
        'rgba(255, 87, 34, 0.85)',     // 深橙红
        'rgba(103, 58, 183, 0.85)',    // 深紫蓝
        'rgba(0, 188, 212, 0.85)',     // 青色
        'rgba(76, 175, 80, 0.85)',     // 绿色
        'rgba(255, 193, 7, 0.85)',     // 黄色
        'rgba(158, 158, 158, 0.85)',   // 灰色
        'rgba(96, 125, 139, 0.85)',    // 蓝灰
        'rgba(171, 71, 188, 0.85)',    // 紫红
        'rgba(255, 64, 129, 0.85)',    // 粉红
        'rgba(0, 172, 193, 0.85)',     // 青蓝
        'rgba(124, 179, 66, 0.85)',    // 橄榄绿
        'rgba(255, 143, 0, 0.85)',     // 深黄
        'rgba(141, 110, 99, 0.85)',    // 棕灰
        'rgba(74, 144, 226, 0.85)'     // 亮蓝
      ]
      return extendedColors[index % extendedColors.length]
    },
    createChart(refName, data, colors, chartType = 'pie') {
      // 修复：正确获取canvas元素
      const canvasEl = this.$refs[refName]
      if (!canvasEl) {
        console.warn(`Canvas element not found for ${refName}`)
        return
      }

      // 销毁已有图表
      if (this.charts[refName]) {
        this.charts[refName].destroy()
      }

      // 处理空数据
      if (!data || data.length === 0) {
        data = [{ name: '暂无数据', value: 1 }]
        colors = ['rgba(128, 128, 128, 0.5)']
      }

      // 确保颜色数组长度匹配数据长度
      const chartColors = []
      for (let i = 0; i < data.length; i++) {
        if (i < colors.length) {
          chartColors.push(colors[i])
        } else {
          chartColors.push(this.getExtendedColor(i - colors.length))
        }
      }

      // 根据图表类型配置不同的选项
      const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        tooltips: {
          enabled: true,
          backgroundColor: 'rgba(15, 20, 25, 0.95)',
          titleFontSize: 13,
          titleFontColor: '#fff',
          titleFontStyle: '500',
          bodyFontSize: 12,
          bodyFontColor: '#e8e8e8',
          borderColor: 'rgba(255, 255, 255, 0.15)',
          borderWidth: 1,
          padding: 15,
          displayColors: true,
          caretSize: 10,
          caretPadding: 12,
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label
            },
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index]
              const value = data.datasets[0].data[tooltipItem.index]
              return `${label}: ${value}`
            }
          }
        },
        animation: {
          duration: 1000,
          easing: 'easeOutCubic'
        },
        hover: {
          mode: 'single',
          onHover: (e, elements) => {
            e.target.style.cursor = elements.length ? 'pointer' : 'default'
          }
        }
      }

      // 根据图表类型设置特定配置
      let chartConfig = {}
      
      if (chartType === 'bar') {
        // 柱状图配置
        chartConfig = {
          type: 'bar',
          data: {
            labels: data.map(d => d.name),
            datasets: [{
              label: '数量',
              data: data.map(d => d.value),
              backgroundColor: chartColors,
              borderColor: chartColors.map(c => c.replace('0.85', '1')),
              borderWidth: 1,
              hoverBackgroundColor: chartColors.map(c => c.replace('0.85', '1')),
              hoverBorderColor: 'rgba(255, 255, 255, 0.8)',
              hoverBorderWidth: 2
            }]
          },
          options: {
            ...baseOptions,
            scales: {
              xAxes: [{
                ticks: {
                  fontColor: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 10,
                  maxRotation: 45,
                  minRotation: 0
                },
                gridLines: {
                  color: 'rgba(255, 255, 255, 0.1)',
                  display: true
                }
              }],
              yAxes: [{
                ticks: {
                  beginAtZero: false,
                  fontColor: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 10
                },
                gridLines: {
                  color: 'rgba(255, 255, 255, 0.1)',
                  display: true
                }
              }]
            },
            animation: {
              ...baseOptions.animation,
              animateScale: false
            }
          }
        }
      } else if (chartType === 'doughnut') {
        // 环形图配置
        chartConfig = {
          type: 'doughnut',
          data: {
            labels: data.map(d => d.name),
            datasets: [{
              data: data.map(d => d.value),
              backgroundColor: chartColors,
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 3,
              hoverBorderWidth: 4,
              hoverBorderColor: 'rgba(255, 255, 255, 0.5)'
            }]
          },
          options: {
            ...baseOptions,
            cutoutPercentage: 50,
            animation: {
              ...baseOptions.animation,
              animateRotate: true,
              animateScale: true
            }
          }
        }
      } else {
        // 饼图配置（默认）
        chartConfig = {
          type: 'pie',
          data: {
            labels: data.map(d => d.name),
            datasets: [{
              data: data.map(d => d.value),
              backgroundColor: chartColors,
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 3,
              hoverBorderWidth: 4,
              hoverBorderColor: 'rgba(255, 255, 255, 0.5)'
            }]
          },
          options: {
            ...baseOptions,
            animation: {
              ...baseOptions.animation,
              animateRotate: true,
              animateScale: true
            }
          }
        }
      }

      // 创建新图表
      this.charts[refName] = new Chart(canvasEl, chartConfig)
    },
  }
}
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  background: #0f1419;
  padding: 1.5rem;
  color: #e8e8e8;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.loading-container i {
  font-size: 2rem;
  margin-bottom: 1rem;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 数据统计区域 - 整体缩小 */
.data-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

/* 缩小统计卡片容器 */
.stat-group {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.25rem;
}

.stat-group-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-total {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.gender-stats {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.gender-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.gender-name {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 400;
}

/* 第一层：今日待验证 */
.top-row {
  margin-bottom: 0.5rem;
}

.top-row .top-card {
  background: rgba(255, 255, 255, 0.02);
  /* border: 1px solid rgba(255, 255, 255, 0.06); */
  border-radius: 12px;
  padding: 0.3rem 1.25rem;
}

/* common header style for all cards */
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.top-row .card-head h4 {
  margin: 0;
  font-size: 0.85rem;
  color: #aaa;
  font-weight: normal;
}

.top-row .card-body {
  flex: 1;
  padding: 0.75rem 1rem;
  overflow-y: auto;
}

/* 今日待验证卡片 */
.unchecked-card .card-body {
  max-height: 180px;
  overflow-y: auto;
}

.unchecked-card .names-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.5rem;
}

.unchecked-card .name-card {
  background: rgba(255,255,255,0.1);
  color: #fff;
  padding: 0.4rem 0.5rem;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  min-height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.unchecked-card .name-card:hover {
  background: rgba(255,255,255,0.2);
}

.unchecked-card .name-card .name-uyghur {
  font-size: 0.9rem;
  font-weight: bold;
  direction: rtl;
  line-height: 1.2;
}

.unchecked-card .status-icon {
  position: absolute;
  top: -4px;
  right: -4px;
  font-size: 0.6rem;
  background: rgba(15, 20, 25, 0.9);
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.unchecked-card .status-icon.el-icon-check {
  color: #52c41a;
}

.unchecked-card .status-icon.el-icon-warning {
  color: #f56c6c;
}

.refresh-btn {
  color: rgba(255,255,255,0.7);
  border: none;
  background: transparent;
  margin-left: 0.5rem; /* add spacing from title */
}

.refresh-btn:hover {
  color: #fff;
}

/* 第二层：最近查看 + 最多查看 */
.bottom-row {
  margin-bottom: 1.5rem;
}

.shared-controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
}

.views-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.views-grid .top-card {
  flex: 1 1 300px;
  min-width: 260px;
  max-width: 48%;
}

.views-grid .top-card {
  background: rgba(255, 255, 255, 0.02);
  /* border: 1px solid rgba(255, 255, 255, 0.06); */
  border-radius: 12px;
  padding: 0.3rem 1.25rem;
}

.views-grid .card-head h4 {
  margin: 0;
  font-size: 0.85rem;
  color: #aaa;
  font-weight: normal;
}

.views-grid .card-body {
  flex: 1;
  padding: 0.75rem 1rem;
  overflow-y: auto;
}

/* 列表项优化 */
.views-list {
  padding: 0.25rem;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-around;
  align-items: center;
  max-height: 150px;
  overflow-y: auto;
}

/* 统一卡片样式 */
.views-item {
  /* background: rgba(255,255,255,0.07); */
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.05);
  width: 80px;
  min-height: 50px;
  padding: 0.6rem 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: background-color 0.2s ease;
}

.views-item:hover {
  background: rgba(255,255,255,0.05);

}

.views-item .name-uyghur {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255,255,255,0.95);
  direction: rtl;
  word-break: break-word;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
}

.views-item .item-meta {
  font-size: 0.6rem;
  color: rgba(255,255,255,0.7);
  font-weight: 500;
  word-break: keep-all;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mini-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: rgba(255,255,255,0.6);
}

.empty {
  color: rgba(255,255,255,0.45);
  padding: 0.6rem;
  font-size: 0.85rem;
  text-align: center;
}



.gender-value {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
}

.gender-percentage {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
}

.total-label {
  font-size: 0.75rem;
  color: #aaa;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.total-value {
  font-size: 1.2rem;
  color: #eee;
  font-weight: 500;
}

/* 缩小卡片网格间距和尺寸 */
.stat-group-content {
  display: grid;
  gap: 1.25rem;
}

.column-3 {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.column-2 {
  grid-template-columns: repeat(2, 1fr);
}

/* 移动端响应式调整 */
@media (max-width: 768px) {
  .dashboard {
    padding: 0.75rem;
  }

  .data-section {
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .stat-group {
    padding: 0.75rem;
  }

  .stat-group-header {
    margin-bottom: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .gender-stats {
    gap: 0.75rem;
  }

  .gender-item {
    padding: 0.25rem 0.5rem;
    gap: 0.25rem;
  }

  .gender-name {
    font-size: 0.65rem;
  }

  .gender-value {
    font-size: 0.75rem;
  }

  .gender-percentage {
    font-size: 0.6rem;
  }

  .top-row .top-card {
    padding: 0.25rem 0.75rem;
  }

  .card-head {
    padding: 0.5rem 0;
  }

  .top-row .card-head h4 {
    font-size: 0.75rem;
  }

  .top-row .card-body {
    padding: 0.5rem 0.75rem;
  }

  .unchecked-card .card-body {
    max-height: 120px;
  }

  .unchecked-card .names-grid {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 0.375rem;
  }

  .unchecked-card .name-card {
    padding: 0.3rem 0.375rem;
    min-height: 40px;
  }

  .unchecked-card .name-card .name-uyghur {
    font-size: 0.8rem;
  }

  .unchecked-card .status-icon {
    width: 14px;
    height: 14px;
    font-size: 0.55rem;
    top: -3px;
    right: -3px;
  }

  .views-grid {
    gap: 0.75rem;
  }

  .views-grid .top-card {
    flex: 1 1 100%;
    min-width: 0;
    max-width: 100%;
    padding: 0.25rem 0.75rem;
  }

  .views-grid .card-head h4 {
    font-size: 0.75rem;
  }

  .views-grid .card-body {
    padding: 0.5rem 0.75rem;
  }

  .views-list {
    gap: 0.375rem;
    max-height: 120px;
  }

  .views-item {
    width: 70px;
    min-height: 45px;
    padding: 0.5rem 0.375rem;
  }

  .views-item .name-uyghur {
    font-size: 0.75rem;
    min-height: 1.75rem;
  }

  .views-item .item-meta {
    font-size: 0.55rem;
  }

  .column-2 {
    grid-template-columns: 1fr !important;
  }

  .stat-group-content {
    gap: 0.75rem;
  }

  .stat-card {
    padding: 0.75rem;
  }

  .card-header {
    margin: 0.5rem 0;
  }

  .card-title {
    font-size: 0.8rem;
  }

  .chart-wrapper {
    height: 100px !important;
    margin-bottom: 0.75rem;
  }

  .chart-legend {
    max-height: 120px !important;
  }

  .chart-legend.legend-double,
  .chart-legend.legend-triple,
  .chart-legend.legend-quad {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 0.25rem !important;
  }

  .legend-item {
    padding: 0.1rem 0.4rem;
    font-size: 0.55rem;
  }

  .legend-name {
    font-size: 0.55rem;
  }

  .legend-value {
    font-size: 0.55rem;
    min-width: 25px;
  }

  .legend-percentage {
    font-size: 0.5rem;
    min-width: 22px;
  }

  .quick-access-section {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;
  }

  .category-card {
    padding: 0.75rem;
  }

  .category-header {
    margin-bottom: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .category-title {
    font-size: 0.75rem;
  }

  .category-items {
    gap: 0.5rem;
  }

  .access-item {
    padding: 0.5rem;
  }

  .access-title {
    font-size: 0.7rem;
  }
}

@media (max-width: 480px) {
  .dashboard {
    padding: 0.5rem;
  }

  .data-section {
    gap: 0.75rem;
  }

  .stat-group {
    padding: 0.5rem;
  }

  .unchecked-card .names-grid {
    grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
    gap: 0.25rem;
  }

  .unchecked-card .name-card {
    padding: 0.25rem 0.3rem;
    min-height: 35px;
  }

  .unchecked-card .name-card .name-uyghur {
    font-size: 0.75rem;
  }

  .unchecked-card .status-icon {
    width: 12px;
    height: 12px;
    font-size: 0.5rem;
  }

  .views-list {
    gap: 0.25rem;
    max-height: 100px;
  }

  .views-item {
    width: 60px;
    min-height: 40px;
    padding: 0.4rem 0.3rem;
  }

  .views-item .name-uyghur {
    font-size: 0.7rem;
    min-height: 1.5rem;
  }

  .views-item .item-meta {
    font-size: 0.5rem;
  }

  .chart-legend.legend-double,
  .chart-legend.legend-triple,
  .chart-legend.legend-quad {
    grid-template-columns: 1fr !important;
  }

  .chart-wrapper {
    height: 80px !important;
  }

  .quick-access-section {
    grid-template-columns: 1fr;
  }

  .category-card {
    padding: 0.5rem;
  }
}

/* 缩小单个统计卡片 */
.stat-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem;
}

.chart-card {
  display: flex;
  flex-direction: column;
}

.card-header {
  margin: 1rem;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: -0.1px;
}

/* 缩小图表容器 */
.chart-wrapper {
  height: 150px;
  margin-bottom: 1rem;
  position: relative;
}

/* 图例区域 - 网格布局 */
.chart-legend {
  display: grid;
  gap: 0.4rem;
  margin-top: auto;
  max-height: 200px;
  overflow-y: auto;
}

/* 单列布局 - 用于项目很少的情况 */
.chart-legend.legend-single {
  grid-template-columns: 1fr;
  gap: 0.35rem;
}

/* 双列布局 - 用于项目较少的情况 */
.chart-legend.legend-double {
  grid-template-columns: repeat(2, 1fr);
  gap: 0.4rem;
}

/* 三列布局 - 用于项目中等的情况 */
.chart-legend.legend-triple {
  grid-template-columns: repeat(3, 1fr);
  gap: 0.35rem;
}

/* 四列布局 - 用于项目很多的情况（如 topics 24个） */
.chart-legend.legend-quad {
  grid-template-columns: repeat(4, 1fr);
  gap: 0.3rem;
}

/* 滚动条样式 */
.chart-legend::-webkit-scrollbar {
  width: 4px;
}

.chart-legend::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.chart-legend::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.chart-legend::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.65rem;
  padding: 0.15rem 0.6rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.legend-item:hover {
  background: rgba(255, 255, 255, 0.05);
}


.legend-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.legend-item:hover .legend-color {
  transform: scale(1.2);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
}

.legend-name {
  flex: 1;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 400;
  transition: color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.legend-item:hover .legend-name {
  color: rgba(255, 255, 255, 0.95);
}

.legend-value {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  min-width: 35px;
  text-align: right;
  font-size: 0.625rem;
  flex-shrink: 0;
}

.legend-percentage {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.6rem;
  min-width: 28px;
  text-align: right;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.legend-item:hover .legend-percentage {
  color: rgba(255, 255, 255, 0.7);
}

/* 快捷入口区域 */
.quick-access-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
}

.category-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.3s ease;
}

.category-card:hover {
  /* transform: translateY(-2px); */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: rgba(255, 255, 255, 0.22);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.category-header i {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
}

.category-title {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.2px;
}

.category-items {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.access-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem;
  border-radius: 8px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
}

.access-item:hover {
  color: rgba(255, 255, 255, 0.9);
  transform: scale(1.02);
}

.access-item i {
  font-size: 0.875rem;
  width: 18px;
  text-align: center;
  transition: color 0.2s ease;
}

.access-title {
  font-size: 0.75rem;
  font-weight: 400;
  transition: color 0.2s ease;
}

/* general top-view/card styles have been removed to reduce duplication; Bashbet-specific layout uses .views-grid and related selectors *//* .mini-loading and .empty styles defined earlier; no need to repeat */

/* RTL 文本样式 */
.rtl-text {
    direction: rtl;
    text-align: right;
    unicode-bidi: bidi-override;
}

/* RTL 文本域样式 */
.rtl-textarea ::v-deep .el-textarea__inner {
    direction: rtl;
    text-align: right;
    unicode-bidi: bidi-override;
}
</style>